<template>
  <div class="map-filters">
    <div class="map-filters__header">
      <p class="map-filters__title">
        Фильтр<span class="map-filters__title-count">Указаны - {{ filtersCount }} параметр</span>
      </p>
      <ArrowIcon
        class="map-filters__arrow"
        :class="{ 'map-popup__arrow_active': visible }"
        @click="emits('toggleFilters')"
      />
    </div>
    <div class="map-filters__types">
      <pCheckbox
          v-for="type in profileTypesList"
          :key="type"
          :class="profileTypes[type].toLowerCase()"
          :modelValue="filters.profileTypes.includes(type)"
          @update:modelValue="toggleType(type)"
          :label="descriptionTypes[type]"
          @input="toggleType(type)"
      />
    </div>
    <div v-if="visible" class="map-filters__hidden filters-list">
      <p class="filters-list__title">Физические данные</p>
      <DigitalBlock
        v-model:first="filters.age.from"
        v-model:second="filters.age.to"
        title="Возраст"
        orText="-"
        :firstPlaceholder="'от 18 лет'"
        :secondPlaceholder="'до 35 лет'"
        @firstInput="(v) => filters.age.from = v"
        @secondInput="(v) => filters.age.to = v"
      />
      <DigitalBlock
        v-model:first="filters.chestSize.from"
        v-model:second="filters.chestSize.to"
        title="Размер груди"
        orText="-"
        :firstPlaceholder="'от 1'"
        :secondPlaceholder="'до 12'"
        @firstInput="(v) => filters.chestSize.from = v"
        @secondInput="(v) => filters.chestSize.to = v"
      />
      <DigitalBlock
        v-model:first="filters.height.from"
        v-model:second="filters.height.to"
        title="Рост"
        orText="-"
        :firstPlaceholder="'от 130 см'"
        :secondPlaceholder="'до 210 см'"
        @firstInput="(v) => filters.height.from = v"
        @secondInput="(v) => filters.height.to = v"
      />
      <DigitalBlock
        v-model:first="filters.weight.from"
        v-model:second="filters.weight.to"
        title="Вес"
        orText="-"
        :firstPlaceholder="'от 30 кг'"
        :secondPlaceholder="'до 100 кг'"
        @firstInput="(v) => filters.weight.from = v"
        @secondInput="(v) => filters.weight.to = v"
      />
      <p class="filters-list__title">Расценки</p>
      <div class="switches">
        <pSwitch
          :firstLabel="'Апартаменты'"
          :secondLabel="'Выезд'"
          :modelValue="filters.priceInfo.type === 'visit'"
          @update:modelValue="togglePriceType"
        />
        <pSwitch
          v-model="filters.priceInfo.isNight"
          :firstLabel="'День'"
          :secondLabel="'Ночь'"
        />
      </div>
      <DigitalBlock
        v-model:first="filters.priceInfo.oneHour.from"
        v-model:second="filters.priceInfo.oneHour.to"
        title="1 час"
        orText="-"
        firstPlaceholder="От ₽"
        secondPlaceholder="До ₽"
        @firstInput="(v) => filters.priceInfo.oneHour.from = v"
        @secondInput="(v) => filters.priceInfo.oneHour.to = v"
      />
      <DigitalBlock
        v-model:first="filters.priceInfo.twoHour.from"
        v-model:second="filters.priceInfo.twoHour.to"
        :title="twoHourLabel"
        orText="-"
        firstPlaceholder="От ₽"
        secondPlaceholder="До ₽"
        @firstInput="(v) => filters.priceInfo.twoHour.from = v"
        @secondInput="(v) => filters.priceInfo.twoHour.to = v"
      />
    </div>
    <div v-if="visible" class="map-filters__action">
      <pButton @click="emits('showProfiles', filters)">Показать</pButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, onMounted, ref, Ref } from "vue";
import ArrowIcon from "@/common/icons/ArrowIcon.vue";
import pCheckbox from '@/common/pCheckbox/pCheckbox.vue';
import { profileTypes } from "@/common/types/profileTypes";
import DigitalBlock from "@/searchSection/components/SearchPanel/DigitalBlock.vue";
import pSwitch from "@/common/pSwitch/pSwitch.vue";
import pButton from "@/common/pButton/pButton.vue";
import { IMapFilters } from "@/mapSearch/types/MapFilters";

defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emits = defineEmits(['toggleFilters', 'showProfiles']);

const descriptionTypes: object = {
  1: 'Индивидуалки',
  2: 'Массажистки',
  3: 'БДСМ',
}

const profileTypesList: ComputedRef<profileTypes[]> = computed(() => {
  return Object.keys(profileTypes)
      .filter((type) => !isNaN(Number(type)))
      .map((type) => Number(type))
})

const filters: Ref<IMapFilters> = ref({
  profileTypes: [1, 2, 3] as number[],
  age: {
    from: '' as string,
    to: '' as string
  },
  chestSize: {
    from: '' as string,
    to: '' as string
  },
  height: {
    from: '' as string,
    to: '' as string
  },
  weight: {
    from: '' as string,
    to: '' as string
  },
  priceInfo: {
    oneHour: {
      from: '' as string,
      to: '' as string
    },
    twoHour: {
      from: '' as string,
      to: '' as string
    },
    isNight: false,
    type: 'home' as 'home' | 'visit',
  },
});

const twoHourLabel = computed(() => {
  return filters.value.priceInfo.isNight ? 'Ночь' : '2 часа'
})

function togglePriceType() {
  filters.value.priceInfo.type = filters.value.priceInfo.type === 'home' ? 'visit' : 'home'
}

onMounted(() => {
  emits('showProfiles', filters.value)
})

const toggleType = (type: number) => {  
  const index = filters.value.profileTypes.indexOf(type);
  if (index === -1) {
    filters.value.profileTypes.push(type);
  } else {
    filters.value.profileTypes.splice(index, 1);
  }
  emits('showProfiles', filters.value)
}

const filtersCount = computed(() => {
  let count = 0;
  
  count += filters.value.profileTypes.length;

  Object.keys(filters.value).forEach((key) => {
    if (filters.value[key].from || filters.value[key].to) {
      if (filters.value[key].from !== '') count++;
      if (filters.value[key].to !== '') count++;
    }
  })

  if (filters.value.priceInfo.type === 'visit') {
    count += 1;
  }

  if (filters.value.priceInfo.isNight) {
    count += 1;
  }
  
  return count;
});
</script>

<style scoped>
.map-filters {
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;

  @media screen and (max-width: 768px) {
    max-width: calc(100% - 40px) !important;
    margin: 0 20px;
  }

  &__header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background: rgb(9, 10, 12);
    border-radius: 16px;

    @media screen and (max-width: 768px) {
      position: relative;
    }
  }

  &__types {
    padding: 0 16px 16px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;

    ::v-deep(.p-checkbox) {
      .p-checkbox__label {
        font-weight: 600;
      }

      &.prostitute {
        .p-icon {
          color: rgba(0, 122, 255, 1);
        }
      }

      &.masseuse {
        .p-icon {
          color: rgba(0, 199, 190, 1);
        }
      }

      &.bdsm {
        .p-icon {
          color: rgba(240, 53, 53, 1);
        }
      }
    }
  }

  &__title {    
    &-count {
      margin-left: 8px;
      color: black;
      background: rgba(89, 236, 52, 1);
      border-radius: 6px;
      padding: 2px 3px;
      font-size: 12px;
      font-weight: 600;
    }
  }

  &__arrow {
    cursor: pointer;

    &_active {
      transform: rotate(180deg);
    }
  }

  .filters-list {
    padding: 0 16px 16px;
    max-height: 415px;
    overflow-y: auto;

    @media screen and (max-width: 768px) {
      max-height: unset;
    }

    &__title {
      margin-bottom: 16px;
    }

    .digital-block {
      margin-bottom: 12px;
      column-gap: 4px;
    }

    .switches {
      display: flex;
      justify-content: space-between;
      margin-bottom: 20px;
      flex-wrap: wrap;
      row-gap: 12px;

      .p-switch {
        display: inline-flex;

      ::v-deep(.p-switch__button) {
        min-width: 38px;
        height: 20px;

        span {
          width: 18px;
          height: 18px;
        }
      }
    }
    }
  }

  &__action {
    padding: 16px;

    .p-button_default {
      width: 100%;
      padding: 12px 0;
    }
  }
}
</style>